import { put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Relationship } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store/internal/actions";
import { EngineState } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store/internal/selectors/ui-state";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { DataSelector, Identifier, TreeEngineState } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/store";
import { TreeEngineSelectors } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";

import { isGroupInstanceArray } from "../../../utilities/documentHelpers";
import { entityInstancePathFromString } from "../../../utilities/pathHelpers";

import { ContactSelectionModalDescriptor } from "../descriptors";
import { SET_CONTACT_DATA } from "../actions";
import { EmpfaengerTyp, isContactSelectionData } from "../types";
import { CONTACT_ENTRY_MODEL, DEFAULT_EMPFAENGER_TYP } from "../const";

const documentService = new DocumentServiceFactory().getDocumentService();

export function* setContactsSaga(): SagaGenerator<void> {
	yield* takeLatest(SET_CONTACT_DATA, handle);
}

function* handle(action: Action<{ activityId: string }>): SagaGenerator<void> {
	const { activityId } = action.payload;
	const treeEngineState = yield* select(TreeEngineSelectors.engineState(activityId));
	const dataState = yield* select(TreeEngineSelectors.dataState(activityId));
	const uiState = yield* select(TreeEngineSelectors.uiState(activityId));
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!treeEngineState || !dataState || !uiState || !activity) {
		return;
	}
	const initiatingActivityId = activity.initiatingActivityId;
	if (!initiatingActivityId || !ContactSelectionModalDescriptor.isInstance(activity.descriptor)) {
		return;
	}
	const { targetGroupPath, entryMode } = activity.descriptor;
	if (!targetGroupPath) {
		return;
	}
	const initiatingActivityData = yield* select(ActivitySelectors.data(initiatingActivityId));
	if (!FormActivity.Data.SingleDocumentData.isInstance(initiatingActivityData)) {
		return;
	}
	const activityData = yield* select(ActivitySelectors.data(activityId));
	const empfaengerTyp: EmpfaengerTyp = isContactSelectionData(activityData)
		? (activityData.empfaengerTyp as EmpfaengerTyp)
		: DEFAULT_EMPFAENGER_TYP;
	const multiSelectionNodes = uiState.multiSelectionNodes;
	const relationIdentifiers = getRelationIdentifiers(multiSelectionNodes);
	const contactEntries = getContactEntriesFromRelationIdentifiers(
		relationIdentifiers,
		dataState.data,
		treeEngineState
	);
	if (contactEntries.length > 0) {
		const formEngineState = yield* select(FormEngineSelectors.engineState(initiatingActivityId));
		if (!formEngineState) {
			return;
		}
		const newDocument = createNewDocument(
			targetGroupPath,
			initiatingActivityData,
			contactEntries,
			empfaengerTyp,
			formEngineState.models.documentModel,
			entryMode
		);
		const screenLocationStack = UiStateSelectors.screenLocationStack()(formEngineState);
		yield* put(ActivityActions.setData({ activityId: initiatingActivityId, data: { document: newDocument } }));
		yield* put(
			FormEngineActions.command({
				engineEvent: Commands.changeScreenState({ index: screenLocationStack.length - 1, dirty: true }),
				activityId: initiatingActivityId
			})
		);
	}
	yield* put(ActivityActions.cancel({ activityId }));
}

function getRelationIdentifiers(multiSelection: TreeEngineState.MultiSelectionNodes): Identifier[] {
	const selectedElements = Object.entries(multiSelection).flatMap(([key, value]) =>
		value === TreeEngineState.MultiSelectionState.SELECTED ? [key] : []
	);
	return selectedElements.reduce<Identifier[]>((result, currentValue) => {
		const path = TreeEngineState.NodePath.fromString(currentValue);
		const linkIdentifier = TreeEngineState.NodePath.toLinkIdentifier(path);
		if (linkIdentifier !== undefined) {
			result.push(linkIdentifier);
		}
		return result;
	}, []);
}

type ContactEntry = { name: string; email: string };

function getContactEntriesFromRelationIdentifiers(
	relationIdentifiers: Identifier[],
	treeEngineData: TreeEngineState.Data,
	treeEngineState: TreeEngineState
): ContactEntry[] {
	return relationIdentifiers.flatMap(identifier => {
		const entity = treeEngineData[identifier.type]?.[identifier.id];
		if (!entity || !TreeEngineState.Link.isAssignableFrom(entity)) {
			return [];
		}
		const entities = (entity.linkRef.linkDescriptor as Relationship.LinkDescriptorResponse).entities;
		const entryEntity = entities.find(e => e.modelName === CONTACT_ENTRY_MODEL && e.role === "entry");
		if (!entryEntity?.docRef) {
			return [];
		}
		const node = DataSelector.node({ id: entryEntity.docRef, type: CONTACT_ENTRY_MODEL })(treeEngineState);
		if (!node) {
			return [];
		}
		const doc = node.document as GroupInstance;
		const name = documentService.getAssignedObject(doc, entityInstancePathFromString("root[1]/name[1]"));
		const email = documentService.getAssignedObject(doc, entityInstancePathFromString("root[1]/email[1]"));
		if (typeof email !== "string") {
			return [];
		}
		return [{ name: typeof name === "string" ? name : "", email }];
	});
}

function createNewDocument(
	documentPathToRepeatableGroup: string,
	initiatingActivityData: FormActivity.Data.SingleDocumentData,
	contacts: ContactEntry[],
	empfaengerTyp: EmpfaengerTyp,
	documentModel: EngineState["models"]["documentModel"],
	entryMode?: string
): GroupInstance {
	const entityInstance = entityInstancePathFromString(documentPathToRepeatableGroup);
	const document = initiatingActivityData.document as GroupInstance;
	const existingData = documentService.getAssignedObject(document, entityInstance);
	const newEntries: GroupInstance[] =
		entryMode === "email"
			? contacts.map(({ email }) => ({ email }))
			: contacts.map(({ email }) => ({ empfaenger: email, empfaengerTyp }));
	const newData = !isGroupInstanceArray(existingData) ? newEntries : [...existingData, ...newEntries];
	return documentService.updateEntityInstance(document, entityInstance, newData, documentModel);
}
